<template>
  <div>
    <div v-if="!zizId" class="alert alert-danger">
      {{ localText.idNotFound }}
    </div>
    <div v-else-if="submitted" :class="$style.success">
      <div :class="$style.successIcon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <p :class="$style.successText">{{ localText.success }}</p>
    </div>
    <div v-else>
      <p>
        {{ localText.intro }}
      </p>
      <p>
        {{ localText.instructions }}
      </p>
      <p>
        {{ localText.light }}
      </p>
      <p>
        <b>{{ localText.warningLabel }}</b>
        {{ localText.warningPrefix }}
        <b>{{ localText.warningStrong }}</b>.
      </p>
      <form @submit.prevent="handleSubmit">
        <div :class="$style.zones">
          <drop-zone
            v-for="(slot, i) in slots"
            :key="i"
            :label="slot.label"
            :file="slot.file"
            :error="slot.error"
            :upload-hint="localText.uploadHint"
            :upload-formats="localText.uploadFormats"
            :delete-label="localText.delete"
            @file-selected="(f) => onFileSelected(i, f)"
            @file-removed="() => onFileRemoved(i)"
          />
        </div>

        <div v-if="error" class="alert alert-danger mt-3">
          {{ localText.error }}
        </div>

        <button
          type="submit"
          class="btn btn-primary mt-4"
          :disabled="loading"
        >
          {{ loading ? localText.sending : localText.submit }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import DropZone from './DropZone.vue';
import { getTranslatedText } from '@/constants/constants';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPT = ['jpg', 'jpeg', 'png', 'tiff', 'tif', 'pdf'];

function validateFile(file, text) {
  if (file.size > MAX_SIZE_BYTES) return text.fileTooLarge;
  const ext = file.name.split('.').pop().toLowerCase();
  if (!ACCEPT.includes(ext)) return text.invalidFileType;
  return null;
}

export default {
  name: 'PhotoConsultComponent',

  components: { DropZone },

  props: {
    zizId: {
      type: String,
      default: '',
    },
    postTo: {
      type: String,
      default: '/receive/photo-consult',
    },
    text: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      slots: [],
      loading: false,
      submitted: false,
      error: false,
    };
  },

  validations: {
    slots: {
      $each: {
        file: { required },
      },
    },
  },

  created() {
    this.slots = [
      { label: this.localText.photo1, file: null, error: '' },
      { label: this.localText.photo2, file: null, error: '' },
    ];
  },

  methods: {
    onFileSelected(index, file) {
      const err = validateFile(file, this.localText);
      this.$set(this.slots, index, { ...this.slots[index], file: err ? null : file, error: err || '' });
    },

    onFileRemoved(index) {
      this.$set(this.slots, index, { ...this.slots[index], file: null, error: '' });
    },

    validate() {
      let valid = true;
      this.slots.forEach((slot, i) => {
        if (!slot.file) {
          this.$set(this.slots, i, { ...slot, error: this.localText.required });
          valid = false;
        }
      });
      return valid;
    },

    async handleSubmit() {
      if (!this.validate()) return;

      this.loading = true;
      this.error = false;

      const formData = new FormData();
      formData.append('zizId', this.zizId);
      formData.append('PhotoConsult[photo1]', this.slots[0].file);
      formData.append('PhotoConsult[photo2]', this.slots[1].file);

      try {
        await this.$http.post(this.postTo, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.submitted = true;
      } catch (_) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    localText() {
      return {
        ...getTranslatedText('photoConsult', this.$store.getters.SITE_LOCALE),
        ...(this.text || {}),
      };
    },
  },
};
</script>

<style lang="scss" module>

.zones {
  display: flex;
  gap: 16px;

  @media (max-width: 576px) {
    flex-direction: column;
  }
}

.success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 40px 0;
  text-align: center;
}

.successIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: $green-100;
  color: $green-700;
  flex-shrink: 0;

  svg {
    width: 40px;
    height: 40px;
  }
}

.successText {
  font-size: 22px;
  font-weight: 600;
  color: $black;
  margin: 0;
}
</style>
